/**
 * RPDM Wave 2-UI · Phase 2 · Hierarchy Tree orchestrator.
 *
 * Read-only rendering of the aggregated hierarchy payload returned by
 * `GET /api/projects/:engagementId/hierarchy`. Renders three sections:
 *
 *   1. Workstreams (each with nested milestones + work items).
 *   2. Orphan milestones (workstream_id is null).
 *   3. Orphan work items (no workstream, no milestone, no parent).
 *
 * Metadata warnings from the backend surface as a discreet banner
 * (super_admin sees the raw codes; other roles see a neutral hint).
 *
 * Deterministic rendering: the backend already returns the tree in a
 * deterministic order (§ deterministic ordering). This component
 * preserves iteration order — no client-side re-sort.
 */
import HierarchyTreeNode from "./HierarchyTreeNode";

export default function HierarchyTree({ tree }) {
    const workstreams = Array.isArray(tree?.workstreams) ? tree.workstreams : [];
    const orphanMilestones = Array.isArray(tree?.orphan_milestones) ? tree.orphan_milestones : [];
    const orphanWorkItems = Array.isArray(tree?.orphan_work_items) ? tree.orphan_work_items : [];
    const warnings = Array.isArray(tree?.meta?.warnings) ? tree.meta.warnings : [];

    return (
        <div
            data-testid="rpdm-hierarchy-tree"
            className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-2"
        >
            {warnings.length > 0 ? (
                <div
                    data-testid="rpdm-hierarchy-warnings"
                    role="note"
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                >
                    Hierarchy contains structural notes: {warnings.join(", ")}
                </div>
            ) : null}

            <ul className="flex flex-col gap-2" role="tree">
                {workstreams.map((ws) => (
                    <HierarchyTreeNode key={ws.id} kind="workstream" node={ws} depth={0} />
                ))}
                {orphanMilestones.map((ms) => (
                    <HierarchyTreeNode key={ms.id} kind="milestone" node={ms} depth={0} />
                ))}
                {orphanWorkItems.map((wi) => (
                    <HierarchyTreeNode key={wi.id} kind="work_item" node={wi} depth={0} />
                ))}
            </ul>
        </div>
    );
}
